import React, { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import ReportsApi from "../Api/ReportsApi";
import ProductApi from "../Api/ProductApi";
import TablePagination from "./TablePagination";
import StandardDatePicker from "./StandardDatePicker";
import usePermissions from "../hook/usePermissions";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const ProductStockLogsLayer = () => {
    const { productId, sku } = useParams();
    const { hasPermission } = usePermissions();
    const location = useLocation();
    const valueIds = new URLSearchParams(location.search).get("valueIds") || "";

    const [product, setProduct] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    useEffect(() => {
        const loadProduct = async () => {
            try {
                const res = await ProductApi.getById(productId);
                if (res.status) {
                    setProduct(res.response.data || res.response);
                }
            } catch (err) {
                console.error("Error loading product name:", err);
            }
        };
        loadProduct();
    }, [productId]);

    useEffect(() => {
        fetchLogs();
    }, [currentPage, rowsPerPage]);

    const fetchLogs = async () => {
        setIsLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: rowsPerPage,
                productId: productId,
                attributeId: sku, // Filter specifically for this variant SKU!
                valueIds: valueIds || undefined,
                fromDate: fromDate || undefined,
                toDate: toDate || undefined
            };
            const res = await ReportsApi.getStockReports(params);
            if (res.status) {
                setReportData(res.response.data || []);
                setTotalRecords(res.response.total || 0);
                setTotalPages(Math.ceil((res.response.total || 0) / rowsPerPage));
            }
        } catch (error) {
            console.error("Error fetching variant logs:", error);
            toast.error("Failed to load transaction logs");
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearFilters = () => {
        setFromDate("");
        setToDate("");
        if (currentPage === 0) {
            // Manually invoke if page is already 0 (as useEffect won't trigger)
            setIsLoading(true);
            const params = {
                page: 0,
                limit: rowsPerPage,
                productId: productId,
                attributeId: sku,
                valueIds: valueIds || undefined
            };
            ReportsApi.getStockReports(params)
                .then(res => {
                    if (res.status) {
                        setReportData(res.response.data || []);
                        setTotalRecords(res.response.total || 0);
                        setTotalPages(Math.ceil((res.response.total || 0) / rowsPerPage));
                    }
                })
                .catch(e => console.error(e))
                .finally(() => setIsLoading(false));
        } else {
            setCurrentPage(0);
        }
    };

    const getVariantText = (item) => {
        const raw = item.variantLabel || item.attributeId || "-";
        if (typeof raw === "string") {
            return raw.replace(/(?:^|,\s*)[^:,]+:\s*/g, "");
        }
        return raw;
    };

    const getReferenceText = (item) => {
        const orderId = item.orderData?.[0]?.orderId || item.orderData?.[0]?.invoiceId;
        if (orderId) return orderId;

        const checkRef = item.referenceLabel || item.referenceId;
        if (checkRef && checkRef !== "N/A") {
            const str = String(checkRef).trim();
            if (/^[0-9a-fA-F]{24}$/.test(str)) {
                return `Order #${str.slice(-6).toUpperCase()} (Deleted)`;
            }
            return str;
        }

        if (item.description && item.description.trim()) return item.description;

        return "N/A";
    };

    const handleExportExcel = async () => {
        toast.info("Preparing Excel file...");
        try {
            const params = {
                page: 0,
                limit: -1,
                productId: productId,
                attributeId: sku,
                valueIds: valueIds || undefined,
                fromDate: fromDate || undefined,
                toDate: toDate || undefined
            };
            const res = await ReportsApi.getStockReports(params);
            if (res.status && res.response.data) {
                const data = res.response.data.map((item, index) => ({
                    "S.No": index + 1,
                    "Date": item.createdAt ? new Date(item.createdAt).toLocaleString() : "-",
                    "Product Name": item.productName || "-",
                    "Variant SKU": item.attributeId || sku || "-",
                    "Variant label": getVariantText(item),
                    "Change": item.quantity > 0 ? `+${item.quantity}` : item.quantity,
                    "Current Stock": item.currentStock || 0,
                    "Type": (item.type || "Manual").toUpperCase(),
                    "Reference": getReferenceText(item),
                    "Created By": item.createdByName || "System",
                    "Description": item.description || "-"
                }));

                const worksheet = XLSX.utils.json_to_sheet(data);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Variant Stock Report");
                XLSX.writeFile(workbook, `Variant_Stock_Logs_${sku}_${new Date().getTime()}.xlsx`);
                toast.success("Excel downloaded successfully");
            } else {
                toast.error("Failed to export variant data");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred during Excel export");
        }
    };

    const getTypeBadge = (type, item) => {
        const t = (type || "").toLowerCase().trim();
        const paymentStatus = item?.orderData?.[0]?.paymentStatus;
        switch (t) {
            case "initial": return <span className="badge bg-info-focus text-info-main px-24 py-4 radius-4">Initial</span>;
            case "order": {
                if (item?.description && item.description.includes("Re-deducted")) {
                    return <span className="badge bg-danger-focus text-danger-main px-24 py-4 radius-4">Order (Re-deducted)</span>;
                }
                if (paymentStatus === "Failed") {
                    return <span className="badge bg-neutral-200 text-neutral-800 px-24 py-4 radius-4">Order (Failed)</span>;
                }
                if (paymentStatus === "Pending") {
                    return <span className="badge bg-warning-focus text-warning-main px-24 py-4 radius-4">Order (Pending)</span>;
                }
                return <span className="badge bg-danger-focus text-danger-main px-24 py-4 radius-4">Order</span>;
            }
            case "stock_update": return <span className="badge bg-success-focus text-success-main px-24 py-4 radius-4">Stock Update</span>;
            case "purchase": return <span className="badge bg-primary-focus text-primary-main px-24 py-4 radius-4">Purchase</span>;
            case "return": return <span className="badge bg-success-focus text-success-main px-24 py-4 radius-4">Return</span>;
            case "cancel": return <span className="badge bg-warning-focus text-warning-main px-24 py-4 radius-4">Cancel</span>;
            case "payment_failed":
            case "payment-failed":
                return <span className="badge bg-warning-focus text-warning-main px-24 py-4 radius-4">Payment Failed (+Restored)</span>;
            default: {
                const label = (type || "Manual").replace(/_/g, " ").replace(/-/g, " ");
                const formatted = label.charAt(0).toUpperCase() + label.slice(1);
                return <span className="badge bg-neutral-200 text-neutral-800 px-24 py-4 radius-4">{formatted}</span>;
            }
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-20">
                    <div>
                        <h6 className="text-primary-600 pb-2 mb-0">Variant Stock logs</h6>
                        {product && (
                            <span className="text-sm text-secondary-light">
                                Product: <strong className="text-dark">{product.name}</strong> | SKU: <strong className="text-dark">{sku}</strong>
                            </span>
                        )}
                    </div>
                    <Link to={`/products/stock-update/${productId}`} className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 radius-8 px-16 h-40-px">
                        <Icon icon="solar:arrow-left-outline" /> Back to Update Stock
                    </Link>
                </div>

                {/* <div className="row g-3 align-items-end">
                    <div className="col-xl col-md-4 col-sm-6">
                        <label className="form-label fw-bold" style={{ fontSize: '14px' }}>From Date</label>
                        <StandardDatePicker name="fromDate" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="form-control radius-8" />
                    </div>
                    <div className="col-xl col-md-4 col-sm-6">
                        <label className="form-label fw-bold" style={{ fontSize: '14px' }}>To Date</label>
                        <StandardDatePicker name="toDate" value={toDate} onChange={(e) => setToDate(e.target.value)} className="form-control radius-8" />
                    </div>
                    <div className="col-xl-auto col-md-4 col-sm-12 d-flex align-items-end gap-2">
                        <button type="button" onClick={() => { setCurrentPage(0); fetchLogs(); }} className="btn btn-primary d-flex align-items-center gap-2 radius-8 h-40-px px-20" style={{ fontSize: '14px' }}>Filter</button>
                        <button type="button" onClick={handleClearFilters} className="btn btn-outline-danger d-flex align-items-center gap-2 radius-8 h-40-px text-nowrap px-16">
                            <Icon icon="solar:filter-remove-bold-duotone" fontSize={20} /> Clear
                        </button>
                    </div>
                </div> */}
            </div>

            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>S.No</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Date</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Product</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Variant</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }} className="text-center">Change</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }} className="text-end pe-20">Curr Stock</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Type</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Reference</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Created By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan="9" className="text-center py-4">Logs loading...</td></tr>
                            ) : reportData.length > 0 ? (
                                reportData.map((data, index) => (
                                    <tr key={index}>
                                        <td>{currentPage * rowsPerPage + index + 1}</td>
                                        <td>{data.createdAt ? new Date(data.createdAt).toLocaleString() : "-"}</td>
                                        <td className="fw-medium text-secondary-light">{data.productName}</td>
                                        <td>{getVariantText(data)}</td>
                                        <td className={`text-center fw-bold ${data.quantity > 0 ? "text-success-main" : "text-danger-main"}`}>
                                            {data.quantity > 0 ? `+${data.quantity}` : data.quantity}
                                        </td>
                                        <td className="text-end pe-20">{data.currentStock}</td>
                                        <td>{getTypeBadge(data.type, data)}</td>
                                        <td>{getReferenceText(data)}</td>
                                        <td><span className="text-secondary-light fw-medium">{data.createdByName || "System"}</span></td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="9" className="text-center py-4 text-secondary-light">No transaction records found for this variant.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-24">
                    <div>
                        {hasPermission("Stock Reports") && (
                            <button type="button" className="btn btn-primary radius-8 px-20 py-11 d-flex align-items-center gap-2" onClick={handleExportExcel} disabled={reportData.length === 0}>
                                <Icon icon="lucide:download" className="text-xl" /> Export Excel
                            </button>
                        )}
                    </div>
                    <div className="flex-grow-1">
                        <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(0); }} totalRecords={totalRecords} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductStockLogsLayer;
